import LoadingSpinner from "@components/loadingSpinner";
import {
  CheckIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  UserIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { useNotificationStore } from "@store/notificationStore";
import { useState } from "react";
import {
  handlePatchBasketTicketName,
  handlePatchOrderTicketName,
} from "../utils/ticketnameUtil";
import { useTranslations } from "next-intl";
import { Badge, Text } from "@components/theming";
import { motion } from "framer-motion";
import { usePrevious, useThemeContext, useWindowSize } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  /**
   * Unique ticket id
   */
  ticketId: number;
  /**
   * Name associated to the ticket. If there is any
   */
  ticketName: string;

  /**
   * is ticket required
   */
  isRequired: boolean;

  /**
   * hide the required/optional badge if using a custom label
   */
  hideBadge?: boolean;

  /**
   * optional success callback
   */
  onSuccess?: (name?: string) => void;

  /**
   * order id if we are in order history
   */
  orderId?: number;

  autofocus?: boolean;
  disabled?: boolean;
}

/**
 * Component for ticket name input
 *
 */
const TicketnameInput = ({
  ticketId,
  ticketName,
  isRequired,
  hideBadge,
  onSuccess,
  orderId,
  autofocus = true,
  disabled,
}: Props) => {
  const [inputName, setInputName] = useState(ticketName);
  const [editing, setEditing] = useState(isRequired && !ticketName);
  const wasEditing = usePrevious(editing).current;
  const [loading, setLoading] = useState(false);
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const utilTranslations = useTranslations("Utils");
  const orderHistoryTranslations = useTranslations("OrderHistory");
  const isShopsite = useIsShopsite();
  const width = useWindowSize().width;
  const theme = useThemeContext();

  const handleSubmit = async () => {
    setLoading(true);
    const name = inputName.trim();

    if (isRequired && name === "") {
      setFailure(utilTranslations("invalidticketname"));
      setLoading(false);
      return;
    }

    if (orderId) {
      await handlePatchOrderTicketName(
        name,
        orderId,
        ticketId,
        setSuccess,
        setFailure,
        orderHistoryTranslations,
        onSuccess
      );
    } else {
      await handlePatchBasketTicketName(
        name,
        ticketId,
        setSuccess,
        setFailure,
        utilTranslations,
        onSuccess
      );
    }

    setLoading(false);
    setEditing(false);
  };

  if (editing)
    return (
      <div className="flex flex-col gap-1">
        {isRequired && !hideBadge ? (
          <Badge
            color="red"
            radius={theme && theme.radius === "none" ? "none" : "full"}
            sharedClasses="w-fit"
          >
            <ExclamationCircleIcon className="h-4 w-4 stroke-2" />
            <Text size="2">{utilTranslations("required")}</Text>
          </Badge>
        ) : (
          <></>
        )}
        <motion.div
          initial={{ marginBottom: 0 }}
          animate={{
            marginBottom: isRequired
              ? "0.25rem"
              : width && width < 768
              ? "1rem"
              : "0.75rem",
          }}
          className={joinClassNames(
            "relative flex w-full sm:w-fit h-9 border ring-1",
            isShopsite
              ? "border-indigo-500 ring-indigo-500 rounded-lg"
              : "border-[color:var(--accent-8)] ring-[color:var(--accent-8)] rounded-[var(--radius-2)]"
          )}
        >
          {isShopsite && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              <UserIcon className="w-5 h-5 text-gray-500" aria-hidden="true" />
            </div>
          )}
          <input
            autoFocus={autofocus}
            data-testid="userticketname"
            id="user-ticket"
            aria-invalid="true"
            aria-describedby="ticket-error"
            type="text"
            placeholder={`${utilTranslations("name")}${
              isRequired ? "" : ` (${utilTranslations("optional")})`
            }`}
            required={isRequired}
            autoComplete="off"
            className={joinClassNames(
              "w-full sm:w-[10.5rem] px-2 focus:outline-none",
              isShopsite
                ? "text-black placeholder:text-gray-500 text-base pl-9 rounded-l-lg"
                : "bg-[color:var(--accent-surface)] text-[color:var(--gray-12)] placeholder:text-[color:var(--gray-10)] text-sm rounded-l-[var(--radius-2)]"
            )}
            value={inputName}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (!isRequired || inputName !== "")) {
                handleSubmit();
              }
            }}
            onChange={(e) => setInputName(e.target.value)}
            onBlur={(e) => {
              if (
                inputName !== "" &&
                e.relatedTarget?.id !== "cancel-edit" &&
                e.relatedTarget?.id !== "submit-edit"
              ) {
                handleSubmit();
              }
            }}
          />
          <button
            id="submit-edit"
            disabled={
              loading || (isRequired && (!inputName || inputName.trim() === ""))
            }
            onClick={handleSubmit}
            className={joinClassNames(
              "flex items-center justify-center aspect-square transition-colors ease-in-out duration-200 shrink-0 w-[2.125rem]",
              isShopsite
                ? "bg-emerald-100/50 hover:bg-emerald-100 disabled:bg-gray-200 text-emerald-600 hover:text-emerald-800 disabled:text-gray-600 rounded-r-lg"
                : "bg-[color:var(--green-a3)] hover:bg-[color:var(--green-a4)] disabled:bg-[color:var(--gray-a3)] text-[color:var(--green-a11)] hover:text-[color:var(--green-a10)] disabled:text-gray-600 rounded-r-[var(--radius-2)]"
            )}
          >
            {loading ? (
              <LoadingSpinner
                size="16"
                color={isShopsite ? "#000000" : "var(--gray-11)"}
              />
            ) : (
              <CheckIcon className="h-4 w-4 stroke-[3]" />
            )}
          </button>
          {!isRequired ? (
            <button
              id="cancel-edit"
              onClick={() => {
                setEditing(false);
                setInputName(ticketName);
              }}
              className={joinClassNames(
                "absolute top-full py-0.5 left-1/2 -translate-x-1/2 text-white text-sm leading-[1.125] px-2",
                isShopsite
                  ? "bg-indigo-500 rounded-b-md"
                  : "bg-[color:var(--accent-8)] rounded-b-[var(--radius-2)]"
              )}
            >
              {utilTranslations("cancel")}
            </button>
          ) : (
            <></>
          )}
        </motion.div>
      </div>
    );

  if (!ticketName) {
    return (
      <motion.div
        initial={{
          marginBottom: wasEditing
            ? width && width < 768
              ? "1rem"
              : "0.75rem"
            : 0,
        }}
        animate={{ marginBottom: 0 }}
        className={joinClassNames(
          "relative",
          isRequired || !isShopsite
            ? "w-full sm:w-max sm:min-w-[12.75rem]"
            : "w-fit"
        )}
      >
        {isShopsite && !isRequired ? (
          // Optional case: a plain text link, not a button - a bordered/
          // filled control always reads as "the action to take" regardless
          // of size or color, which is wrong for something most customers
          // can skip. This also sidesteps needing to sit next to the
          // area/section badge, which is dynamic-width and can wrap.
          <button
            onClick={() => setEditing(true)}
            disabled={disabled}
            className="flex items-center gap-1 py-1.5 disabled:cursor-not-allowed text-gray-500 hover:[&:not(:disabled)]:text-indigo-600 disabled:text-gray-400 transition-colors ease-in-out duration-200"
          >
            <UserPlusIcon className="w-5 h-5 stroke-[1.5]" />
            <Text
              as="span"
              sharedClasses="text-sm underline underline-offset-2 decoration-gray-300"
            >
              {utilTranslations("addnametoticket")}
            </Text>
          </button>
        ) : (
          <button
            onClick={() => setEditing(true)}
            disabled={disabled}
            className={joinClassNames(
              "flex items-center justify-center gap-1 h-9 w-full px-2 disabled:cursor-not-allowed transition-colors ease-in-out duration-200",
              isShopsite
                ? "bg-indigo-100 hover:[&:not(:disabled)]:bg-indigo-500 hover:[&:not(:disabled)]:text-white disabled:bg-gray-100 text-indigo-800 disabled:text-gray-800/50 font-medium rounded-lg"
                : "bg-[color:var(--accent-a3)] hover:[&:not(:disabled)]:bg-[color:var(--accent-a4)] text-[color:var(--accent-a11)] disabled:opacity-50 rounded-[var(--radius-2)]"
            )}
          >
            <UserPlusIcon className="w-4 h-4 stroke-2" />
            <Text as="span" size="2" weight="medium">
              {utilTranslations("addnametoticket")}
            </Text>
          </button>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{
        marginBottom: wasEditing
          ? width && width < 768
            ? "1rem"
            : "0.75rem"
          : 0,
      }}
      animate={{ marginBottom: 0 }}
      className={joinClassNames(
        "flex items-center font-medium h-9 w-full sm:w-fit border",
        isShopsite
          ? "bg-indigo-100 text-indigo-800 border-indigo-500 rounded-lg"
          : "bg-[color:var(--accent-surface)] text-[color:var(--accent-11)] border-[color:var(--accent-8)] rounded-[var(--radius-2)]"
      )}
    >
      <Text
        as="span"
        size="2"
        sharedClasses="px-2 font-medium text-left w-full sm:w-[10.5rem] truncate"
      >
        {ticketName}
      </Text>
      <button
        aria-label="edit-name"
        onClick={(e) => {
          e.preventDefault();
          setEditing(true);
        }}
        className="flex items-center justify-center h-full w-[2.125rem]"
      >
        <PencilSquareIcon className="w-5 h-5" />
      </button>
    </motion.div>
  );
};

export default TicketnameInput;
